/**
 * Livacils Animations
 * Scroll-triggered animations, counters, parallax, sticky header, before/after slider
 */

(function () {
  'use strict';

  // ─── Design mode: skip all fade-in animations in Theme Editor ───
  if (window.Shopify && Shopify.designMode) {
    document.querySelectorAll('[data-fade-in], [data-fade-in-stagger]').forEach((el) => {
      el.setAttribute('data-visible', 'true');
    });
  }

  // ─── Fade-in on scroll ───────────────────────────────────────────
  const fadeObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-visible', 'true');
          fadeObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -80px 0px' }
  );

  document.querySelectorAll('[data-fade-in], [data-fade-in-stagger]').forEach((el) => {
    fadeObserver.observe(el);
  });

  // ─── Animated counters ───────────────────────────────────────────
  const counterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const el = entry.target;
        const target = parseInt(el.dataset.counter, 10);
        const suffix = el.dataset.counterSuffix || '';
        const duration = 1500;
        const start = performance.now();

        const animate = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          const current = Math.floor(eased * target);
          el.textContent = current.toLocaleString('fr-FR') + suffix;
          if (progress < 1) requestAnimationFrame(animate);
        };

        requestAnimationFrame(animate);
        counterObserver.unobserve(el);
      });
    },
    { threshold: 0.5 }
  );

  document.querySelectorAll('[data-counter]').forEach((el) => counterObserver.observe(el));

  // ─── Sticky header ───────────────────────────────────────────────
  const header = document.querySelector('[data-header]');
  if (header) {
    let lastScrollY = 0;
    let ticking = false;
    const COMPACT_THRESHOLD = 80;
    const HIDE_THRESHOLD = 180;

    // Set CSS variable for mega menu positioning
    const setHeaderHeight = () => {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    };
    setHeaderHeight();

    const updateHeader = () => {
      const currentY = window.scrollY;

      if (currentY > COMPACT_THRESHOLD) {
        header.setAttribute('data-compact', 'true');
      } else {
        header.removeAttribute('data-compact');
      }

      if (currentY > HIDE_THRESHOLD) {
        if (currentY > lastScrollY) {
          header.setAttribute('data-hidden', 'true');
        } else {
          header.removeAttribute('data-hidden');
        }
      } else {
        header.removeAttribute('data-hidden');
      }

      lastScrollY = currentY;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          requestAnimationFrame(updateHeader);
          ticking = true;
        }
      },
      { passive: true }
    );

    window.addEventListener('resize', setHeaderHeight, { passive: true });
  }

  // ─── Hero parallax ───────────────────────────────────────────────
  const heroImage = document.querySelector('[data-hero-image]');
  if (heroImage && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    let parallaxTicking = false;

    window.addEventListener(
      'scroll',
      () => {
        if (!parallaxTicking) {
          requestAnimationFrame(() => {
            const scrolled = window.scrollY;
            if (scrolled < window.innerHeight * 1.5) {
              heroImage.style.transform = `translateY(${scrolled * 0.25}px)`;
            }
            parallaxTicking = false;
          });
          parallaxTicking = true;
        }
      },
      { passive: true }
    );
  }

  // ─── Before/After slider ─────────────────────────────────────────
  class BeforeAfterSlider {
    constructor(element) {
      this.el = element;
      this.after = element.querySelector('[data-before-after-after]');
      this.slider = element.querySelector('[data-before-after-slider]');
      if (!this.after || !this.slider) return;
      this.isDragging = false;
      this.init();
    }

    init() {
      this.slider.addEventListener('mousedown', this.startDrag.bind(this));
      this.slider.addEventListener('touchstart', this.startDrag.bind(this), { passive: true });
      document.addEventListener('mousemove', this.drag.bind(this));
      document.addEventListener('touchmove', this.drag.bind(this), { passive: true });
      document.addEventListener('mouseup', this.stopDrag.bind(this));
      document.addEventListener('touchend', this.stopDrag.bind(this));

      // Keyboard support
      this.slider.addEventListener('keydown', (e) => {
        const current = parseFloat(this.after.style.width) || 50;
        if (e.key === 'ArrowLeft') this.setPosition(Math.max(0, current - 5));
        if (e.key === 'ArrowRight') this.setPosition(Math.min(100, current + 5));
      });
    }

    startDrag(e) {
      this.isDragging = true;
      if (e.type === 'mousedown') e.preventDefault();
    }

    drag(e) {
      if (!this.isDragging) return;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const rect = this.el.getBoundingClientRect();
      const position = ((clientX - rect.left) / rect.width) * 100;
      this.setPosition(Math.max(0, Math.min(100, position)));
    }

    setPosition(pct) {
      this.after.style.width = `${pct}%`;
      this.slider.style.left = `${pct}%`;
    }

    stopDrag() {
      this.isDragging = false;
    }
  }

  document.querySelectorAll('[data-before-after]').forEach((el) => new BeforeAfterSlider(el));

  // ─── Accordion ───────────────────────────────────────────────────
  document.querySelectorAll('.accordion__trigger').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const content = trigger.nextElementSibling;
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

      // Close siblings if in exclusive group
      if (trigger.closest('[data-accordion-exclusive]')) {
        trigger
          .closest('[data-accordion-exclusive]')
          .querySelectorAll('.accordion__trigger[aria-expanded="true"]')
          .forEach((t) => {
            if (t !== trigger) {
              t.setAttribute('aria-expanded', 'false');
              const c = t.nextElementSibling;
              if (c) c.removeAttribute('data-open');
            }
          });
      }

      trigger.setAttribute('aria-expanded', String(!isExpanded));
      if (content) {
        if (isExpanded) {
          content.removeAttribute('data-open');
        } else {
          content.setAttribute('data-open', 'true');
        }
      }
    });
  });

  // ─── Haptic feedback ─────────────────────────────────────────────
  const supportsHaptics = 'vibrate' in navigator;

  function triggerHaptic(intensity = 'light') {
    if (!supportsHaptics) return;
    const patterns = { light: 10, medium: 20, heavy: [30, 10, 30], success: [10, 30, 10] };
    navigator.vibrate(patterns[intensity] || 10);
  }

  document.querySelectorAll('.btn-primary').forEach((btn) => {
    btn.addEventListener('click', () => triggerHaptic('light'));
  });

  const atcBtn = document.querySelector('[data-add-to-cart]');
  if (atcBtn) {
    atcBtn.addEventListener('click', () => triggerHaptic('success'));
  }

  // ─── Sticky Add to Cart (mobile) ─────────────────────────────────
  const stickyAtc = document.querySelector('[data-sticky-atc]');
  const mainAtc = document.querySelector('[data-main-add-to-cart]');

  if (stickyAtc && mainAtc) {
    const stickyObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            stickyAtc.setAttribute('data-visible', 'true');
          } else {
            stickyAtc.removeAttribute('data-visible');
          }
        });
      },
      { threshold: 0 }
    );

    stickyObserver.observe(mainAtc);

    // Sync the sticky ATC with main form
    stickyAtc.querySelector('[data-add-to-cart-trigger]')?.addEventListener('click', () => {
      mainAtc.click();
    });
  }

  // ─── Announcement bar rotation ───────────────────────────────────
  const announcementItems = document.querySelectorAll('[data-announcement-item]');
  if (announcementItems.length > 1) {
    let currentIdx = 0;
    setInterval(() => {
      announcementItems[currentIdx].setAttribute('data-visible', 'false');
      currentIdx = (currentIdx + 1) % announcementItems.length;
      announcementItems[currentIdx].setAttribute('data-visible', 'true');
    }, 4000);
  }

  // ─── Variant selector ────────────────────────────────────────────
  document.querySelectorAll('[data-variant-select]').forEach((select) => {
    const form = select.closest('form');
    const priceEl = form?.querySelector('[data-variant-price]');
    const comparePriceEl = form?.querySelector('[data-compare-price]');
    const atcBtn = form?.querySelector('[data-add-to-cart]');

    select.addEventListener('change', () => {
      const selected = select.options[select.selectedIndex];
      const price = selected.dataset.price;
      const comparePrice = selected.dataset.comparePrice;
      const available = selected.dataset.available === 'true';

      if (priceEl && price) {
        priceEl.textContent = price;
      }

      if (comparePriceEl) {
        if (comparePrice && parseFloat(comparePrice) > parseFloat(price)) {
          comparePriceEl.textContent = comparePrice;
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      if (atcBtn) {
        if (!available) {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Épuisé';
        } else {
          atcBtn.disabled = false;
          atcBtn.textContent = 'Ajouter au panier';
        }
      }
    });
  });

  // ─── Visual variant buttons ──────────────────────────────────────
  document.querySelectorAll('[data-variant-btn-group]').forEach((group) => {
    const hiddenSelect = document.querySelector('#' + group.dataset.variantBtnGroup);
    group.querySelectorAll('[data-variant-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        group.querySelectorAll('[data-variant-value]').forEach((b) => b.removeAttribute('data-selected'));
        btn.setAttribute('data-selected', 'true');
        if (hiddenSelect) {
          hiddenSelect.value = btn.dataset.variantValue;
          hiddenSelect.dispatchEvent(new Event('change'));
        }
      });
    });
  });

  // ─── Gallery image switcher ──────────────────────────────────────
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const mainImage = gallery.querySelector('[data-gallery-main]');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');

    if (!mainImage) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.gallerySrc;
        const srcset = thumb.dataset.gallerySrcset;
        if (src) mainImage.src = src;
        if (srcset) mainImage.srcset = srcset;
        mainImage.alt = thumb.alt || '';
        thumbs.forEach((t) => t.removeAttribute('data-active'));
        thumb.setAttribute('data-active', 'true');
      });
    });
  });

  // ─── Wishlist (localStorage) ─────────────────────────────────────
  const getWishlist = () => {
    try {
      return JSON.parse(localStorage.getItem('livacils_wishlist') || '[]');
    } catch {
      return [];
    }
  };

  const saveWishlist = (list) => {
    try {
      localStorage.setItem('livacils_wishlist', JSON.stringify(list));
    } catch {}
  };

  const updateWishlistBtns = () => {
    const list = getWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const id = parseInt(btn.dataset.wishlistToggle, 10);
      btn.setAttribute('data-active', String(list.includes(id)));
      const label = list.includes(id)
        ? 'Retirer de la wishlist'
        : 'Ajouter à la wishlist';
      btn.setAttribute('aria-label', label);
    });
  };

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const id = parseInt(btn.dataset.wishlistToggle, 10);
    let list = getWishlist();
    if (list.includes(id)) {
      list = list.filter((i) => i !== id);
      window.showToast?.('Retiré de ta wishlist');
    } else {
      list.push(id);
      window.showToast?.('Ajouté à ta wishlist');
    }
    saveWishlist(list);
    updateWishlistBtns();
    triggerHaptic('light');
  });

  updateWishlistBtns();

})();
